import { getGa4MeasurementId } from '../config';
import { createDeferredLoader, loadScript } from './deferredLoader';
import type { AnalyticsProvider } from './types';

const GA4_PROVIDER_NAME = 'Google Analytics';
const GA4_SCRIPT_ID = 'ga4-gtag';

const GA4_MAX_NAME_LENGTH = 40;
const GA4_MAX_STRING_VALUE_LENGTH = 100;
const GA4_MAX_EVENT_PARAMS = 25;

const PAGE_VIEW_EVENT = 'page_view';

const loader = createDeferredLoader({ name: GA4_PROVIDER_NAME });

/**
 * gtag.js recognises a queued command by the pushed value being an `arguments` object rather than an array,
 * so this cannot be an arrow function spreading rest parameters.
 */
function gtag(_command: string, ..._args: unknown[]) {
  window.dataLayer?.push(arguments);
}

/**
 * GA4 rejects names outside [A-Za-z0-9_] and names that do not start with a letter. Word boundaries in
 * camelCase are kept as underscores so `blockCount` reports as `block_count` rather than `blockcount`.
 */
const toGa4Name = (name: string): string =>
  name
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    .replace(/[^A-Za-z0-9_]+/g, '_')
    .replace(/^[^A-Za-z]+/, '')
    .toLowerCase()
    .slice(0, GA4_MAX_NAME_LENGTH);

const toGa4Value = (value: unknown): string | number | boolean =>
  typeof value === 'string' ? value.slice(0, GA4_MAX_STRING_VALUE_LENGTH) : (value as number | boolean);

const PII_PARAM_NAME = /email|phone|first_?name|last_?name|user_?name/i;

/**
 * Google's terms prohibit uploading personally identifiable information, and some events carry
 * properties such as `user.email`, so identifying parameters are dropped by name and by anything that looks
 * like an address.
 */
const isPersonallyIdentifying = (name: string, value: string | number | boolean): boolean =>
  PII_PARAM_NAME.test(name) || (typeof value === 'string' && value.includes('@'));

const toGa4Params = (properties: Record<string, unknown> | undefined): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(properties ?? {})
      .filter(
        (entry): entry is [string, string | number | boolean] =>
          typeof entry[1] === 'string' || typeof entry[1] === 'number' || typeof entry[1] === 'boolean',
      )
      .filter(([name, value]) => !isPersonallyIdentifying(name, value))
      .map(([name, value]): [string, unknown] => [toGa4Name(name), toGa4Value(value)])
      // A name of only digits or punctuation sanitises to nothing, which GA4 would reject.
      .filter(([name]) => name !== '')
      .slice(0, GA4_MAX_EVENT_PARAMS),
  );

export const ga4Provider: AnalyticsProvider = {
  name: GA4_PROVIDER_NAME,

  load: () => {
    const measurementId = getGa4MeasurementId();
    if (!measurementId) {
      return;
    }

    loader.start(async () => {
      window.dataLayer = window.dataLayer ?? [];
      await loadScript({ id: GA4_SCRIPT_ID, src: `https://www.googletagmanager.com/gtag/js?id=${measurementId}` });
      gtag('js', new Date());
      // Page views are reported explicitly from the router listeners, so the automatic one would double-count.
      gtag('config', measurementId, { send_page_view: false });
    });
  },

  /** Only the user id is sent. Google's terms prohibit uploading personally identifiable information. */
  identify: ({ userId }) => loader.enqueue(() => gtag('set', { user_id: userId })),

  /** GA4 has no group concept. */
  group: () => undefined,

  page: ({ title }) =>
    loader.enqueue(() => gtag('event', PAGE_VIEW_EVENT, { page_title: title, page_location: window.location.href })),

  track: ({ name, properties }) => loader.enqueue(() => gtag('event', toGa4Name(name), toGa4Params(properties))),

  /**
   * GA4 has no session teardown, so only the user id is cleared. The device keeps its client id, which is
   * what the cookie banner clears when consent is withdrawn.
   */
  reset: () => loader.enqueue(() => gtag('set', { user_id: null })),
};
